<template>
  <div class="ic-auto-optout">
    <div class="ic-auto-optout__form">
      <div class="ic-auto-optout__form-group">
        <h2 class="ic-auto-optout__step-title">Matching Records</h2>
        <TzMarkDown
          class="ic-auto-optout__step-subtitle"
          content="Select the record that describes you:"
        />
      </div>
      <div
        v-for="(person, index) in people"
        :key="`person_${index}`"
        :class="[
          'ic-auto-optout__person-summary',
          { 'is-selected': selectedCid === person.cid }
        ]"
      >
        <label
          :for="`record_${index}`"
          class="ic-auto-optout__person-summary-label"
        >
          <div class="ic-auto-optout__radio-wrapper">
            <input
              type="radio"
              :id="`record_${index}`"
              name="selectedRecord"
              class="ic-auto-optout__radio"
              :value="person.cid"
              v-model="selectedCid"
            />

            <div class="ic-auto-optout__person-summary-content">
              <div class="ic-auto-optout__person-summary-header">
                <p class="ic-auto-optout__person-summary-id">
                  <strong>ID:</strong> {{ person.id }}
                </p>
                <h3 class="ic-auto-optout__person-summary-name">
                  {{
                    formatNameCaseWithSplitterAndSpacer({
                      name: `${person.firstname} ${person.middlename} ${person.lastname}`
                    })
                  }}
                  <span v-if="person.dob"> ({{ person.dob }}) </span>
                </h3>
                <div
                  class="ic-auto-optout__person-summary-section"
                  v-if="person.aliases?.length"
                >
                  <div class="ic-auto-optout__grid-list">
                    <p v-for="(alias, i) in person.aliases" :key="`alias_${i}`">
                      {{ formatNameCaseWithSplitterAndSpacer({ name: alias }) }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="ic-auto-optout__person-summary-stacked">
                <div
                  class="ic-auto-optout__person-summary-section"
                  v-if="person.addresses"
                >
                  <h4>LOCATIONS</h4>
                  <div class="ic-auto-optout__grid-list">
                    <p
                      v-for="(address, i) in person.addresses"
                      :key="`address_${i}`"
                    >
                      {{ address.full }}
                    </p>
                  </div>
                </div>

                <div
                  class="ic-auto-optout__person-summary-section"
                  v-if="person.emails"
                >
                  <h4>EMAIL ADDRESSES</h4>
                  <div class="ic-auto-optout__grid-list">
                    <p
                      v-for="(email, i) in person.emails"
                      :key="`email_${i}`"
                      class="ic-auto-optout__email-truncate"
                    >
                      {{ email.full }}
                    </p>
                  </div>
                </div>

                <div
                  class="ic-auto-optout__person-summary-section"
                  v-if="person.phones"
                >
                  <h4>PHONE NUMBERS</h4>
                  <div class="ic-auto-optout__grid-list">
                    <p v-for="(phone, i) in person.phones" :key="`phone_${i}`">
                      {{ phone.full }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </label>
      </div>

      <TzMarkDown
        v-if="!people.length"
        class="ic-auto-optout__no-results"
        content="We were unable to find any results that matched your name and date of birth."
      />

      <div class="ic-auto-optout__form-group">
        <div class="ic-auto-optout__button-group">
          <button
            type="button"
            class="ic-auto-optout__button ic-auto-optout__button--back"
            @click="onBack"
          >
            Back
          </button>

          <button
            type="button"
            class="ic-auto-optout__button"
            :disabled="!canContinue || isSubmitting"
            @click="onContinue"
          >
            <span>{{ isSubmitting ? 'Loading...' : 'Continue' }}</span>
          </button>
        </div>
      </div>

      <div class="ic-auto-optout__form-group">
        <div class="ic-auto-optout__disclaimer">
          <h4>WHAT IF NONE OF THESE ARE ME?</h4>
          <p>
            There are a number of reasons that there may not be a result that
            references you. Records relating to you may be missing in the
            datasets we use to build identities, or they contained insufficient
            information to construct an identity.
          </p>
          <p>
            It's also possible that a report that references you was suppressed
            through other means, or by someone with a different email address.
            In this scenario, we are unable to display suppressed results unless
            it matches verified elements in your identity. If you wish to gain
            control over such a report, build and verify as complete an identity
            as possible in the identity editor.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TzMarkDown from '@trazi/tz-markdown/src/tz-markdown.vue';
import formatNameCaseWithSplitterAndSpacer from '@/assets/js/shared/helpers/formatNameCaseWithSplitterAndSpacer';

import searchSDK from '@/assets/js/shared/services/search-sdk';
import optOutSDK from '@/assets/js/shared/services/opt-out-sdk';

const { LocalPeopleFull } = searchSDK;

export default {
  name: 'optout-results',
  components: {
    TzMarkDown
  },
  data() {
    return {
      people: [],
      selectedCid: null,
      verifiedEmail: null,
      tokenValid: false,
      tokenError: null,
      isSubmitting: false,
      formatNameCaseWithSplitterAndSpacer
    };
  },
  computed: {
    canContinue() {
      return this.selectedCid;
    }
  },
  methods: {
    redirectToStart() {
      this.$router.push({ name: 'optout' });
    },
    selectRecord(cid) {
      this.selectedCid = cid;
    },
    onBack() {
      const resolved = this.$router.resolve({
        name: 'optout.name',
        params: { token: this.$route.params.token },
        query: this.$route.query
      });
      window.location.href = resolved.href;
    },
    onContinue() {
      this.isSubmitting = true;
      // Go to verification page with selected record
      const query = { ...this.$route.query };
      if (this.selectedCid) {
        query.selectedCid = this.selectedCid;
      }
      // Use router to resolve the correct URL, then assign to window.location.href for SSR
      const resolved = this.$router.resolve({
        name: 'optout.verification',
        params: { token: this.$route.params.token },
        query
      });
      window.location.href = resolved.href;
    }
  },
  async asyncData({ app, route, redirect, $bugsnag, req }) {
    // Validate token first
    const token = route.params.token;

    if (!token) {
      const resolved = app.router.resolve({ name: 'optout' });
      redirect(resolved.href);
      return;
    }

    const validation = await optOutSDK().Verifications.byToken(token, 'email');

    if (!validation || validation.type !== 'success') {
      const resolved = app.router.resolve({ name: 'optout' });
      redirect(resolved.href);
      return;
    }

    // Use the verified email and DOB from the token
    const verifiedEmail = validation.data?.email;
    const requestorDob = validation.data?.dob;

    try {
      const response = await LocalPeopleFull.byEmail({
        email: verifiedEmail,
        limit: 10,
        filter:
          'id,cid,firstname,lastname,middlename,dob,aliases,addresses,emails,phones,optout'
      });

      const maskedPeople = response.map(person => {
        // Check if DOB matches for this person
        const isMatch = person.dob === requestorDob;

        // Format DoB
        const formattedDob = isMatch ? person.dob : person.dob?.split('-')[0];

        // Format addresses
        const limit = isMatch ? 6 : 9;
        const formattedAddresses = (person.addresses || [])
          .slice(0, limit)
          .map(address => {
            const cityStateZip = `${formatNameCaseWithSplitterAndSpacer({
              name: address.city
            })}, ${address.state} ${address.zip}`;

            if (!isMatch) return { full: cityStateZip };

            const fullStreet = [
              address.street_number,
              formatNameCaseWithSplitterAndSpacer({
                name: address.street_name
              }),
              formatNameCaseWithSplitterAndSpacer({
                name: address.street_type
              }),
              formatNameCaseWithSplitterAndSpacer({
                name: address.street_affix || ''
              })
            ]
              .filter(Boolean)
              .join(' ')
              .trim();

            return { full: `${fullStreet}, ${cityStateZip}` };
          });

        // Masked emails
        const emails = (person.emails || []).slice(0, 6).map(email => {
          const [user, domain] = email.full.toLowerCase().split('@');
          const visible = user.length <= 3 ? user : user.slice(0, 3);
          return {
            full: isMatch ? email.full.toLowerCase() : `${visible}***@${domain}`
          };
        });

        // Masked phones
        const phones = (person.phones || []).slice(0, 6).map(phone => {
          const str = String(phone.full).padStart(10, '0');
          const area = str.slice(0, 3);
          const mid = str.slice(3, 6);
          const end = str.slice(6);
          return {
            full: isMatch
              ? `(${area}) ${mid}-${end}`
              : `(${area}) ***-**${end.slice(-2)}`
          };
        });

        return {
          ...person,
          dob: formattedDob,
          addresses: formattedAddresses,
          emails,
          phones
        };
      });

      return { people: maskedPeople };
    } catch (err) {
      $bugsnag?.notify(err, event => {
        event.addMetadata('vue', {
          page: 'auto-optout/step-matching-results.vue',
          method: 'asyncData',
          routeName: route?.name,
          token: route.params.token
        });
      });
      console.error('[SSR Search Error]', err);
      return { people: [] };
    }
  },
  head() {
    const siteName = 'Information.com';

    return {
      title: `Opt-Out (Remove My Information) | ${siteName}`,
      meta: [
        {
          name: 'description',
          hid: 'description',
          content: `${siteName} lays out an easy to follow process to grant you the ability to remove your information from our people results. Most requests will take effect within 5-7 days of submission.`
        },
        {
          property: 'og:title',
          hid: 'og:title',
          content: `Opt-Out (Remove My Information) | ${siteName}`
        },
        {
          property: 'apple-mobile-web-app-title',
          hid: 'apple-mobile-web-app-title',
          content: `Opt-Out (Remove My Information) | ${siteName}`
        },
        {
          property: 'og:description',
          hid: 'og:description',
          content: `${siteName} lays out an easy to follow process to grant you the ability to remove your information from our people results. Most requests will take effect within 5-7 days of submission.`
        },
        {
          property: 'og:url',
          hid: 'og:url',
          content: `${siteName}/contact/`
        }
      ]
    };
  }
};
</script>
